import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '@shared/services/api/ApiService';

interface QRLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (tokens: { accessToken: string; refreshToken: string }) => void;
}

interface QRLoginSession {
  sessionId: string;
  qrCodeUrl: string;
  expiresAt: string;
}

interface QRLoginStatus {
  status: 'pending' | 'confirmed' | 'expired' | 'cancelled';
  user?: any;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
}

const QRLoginModal: React.FC<QRLoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [qrSession, setQrSession] = useState<QRLoginSession | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'checking' | 'success' | 'expired' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // 生成二维码
  const generateQRCode = async () => {
    try {
      setStatus('loading');
      setError('');

      const response = await apiService.post('/auth/wechat/qr-login/generate');

      if (response.success) {
        setQrSession(response.data);
        setStatus('ready');

        // 计算倒计时
        const expiresAt = new Date(response.data.expiresAt);
        const now = new Date();
        const timeLeft = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);
        setCountdown(Math.max(0, timeLeft));

        // 开始轮询状态
        startStatusPolling(response.data.sessionId);

        // 开始倒计时
        startCountdown();
      } else {
        setError('生成二维码失败');
        setStatus('error');
      }
    } catch (err: any) {
      console.error('Generate QR code failed:', err);
      setError(err.message || '生成二维码失败');
      setStatus('error');
    }
  };

  // 开始状态轮询
  const startStatusPolling = (sessionId: string) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(async () => {
      try {
        const response = await apiService.get(`/auth/wechat/qr-login/status/${sessionId}`);

        if (response.success) {
          const statusData: QRLoginStatus = response.data;

          if (statusData.status === 'confirmed') {
            // 检查tokens - 后端返回的结构是 response.data.tokens
            const tokens = statusData.tokens;

            if (tokens?.accessToken && tokens?.refreshToken) {
              setStatus('success');
              stopPolling();
              onSuccess(tokens);
            }
          } else if (statusData.status === 'expired') {
            setStatus('expired');
            stopPolling();
          } else if (statusData.status === 'cancelled') {
            setStatus('expired');
            stopPolling();
          }
        }
      } catch (err) {
        console.error('Check QR login status failed:', err);
      }
    }, 2000); // 每2秒检查一次
  };

  // 开始倒计时
  const startCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setStatus('expired');
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 停止轮询
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // 刷新二维码
  const refreshQRCode = () => {
    stopPolling();
    generateQRCode();
  };

  // 格式化倒计时显示
  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 组件挂载时生成二维码
  useEffect(() => {
    if (isOpen) {
      generateQRCode();
    } else {
      stopPolling();
      setQrSession(null);
      setStatus('loading');
      setError('');
      setCountdown(0);
    }

    return () => {
      stopPolling();
    };
  }, [isOpen]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        {/* 标题 */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            微信扫码登录
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容区域 */}
        <div className="text-center">
          {status === 'loading' && (
            <div className="py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">正在生成二维码...</p>
            </div>
          )}

          {status === 'ready' && qrSession && (
            <div>
              <div className="mb-4">
                <img
                  src={qrSession.qrCodeUrl}
                  alt="扫码登录"
                  className="w-48 h-48 mx-auto border border-gray-200 dark:border-gray-600 rounded-lg"
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                请使用微信扫描上方二维码
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                二维码将在 {formatCountdown(countdown)} 后过期
              </p>
            </div>
          )}

          {status === 'checking' && (
            <div className="py-8">
              <div className="animate-pulse">
                <div className="w-48 h-48 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-4"></div>
              </div>
              <p className="text-gray-600 dark:text-gray-400">正在确认登录...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-green-600 font-medium">登录成功！</p>
            </div>
          )}

          {status === 'expired' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">二维码已过期</p>
              <button
                onClick={refreshQRCode}
                className="btn btn-primary"
              >
                刷新二维码
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={refreshQRCode}
                className="btn btn-primary"
              >
                重新生成
              </button>
            </div>
          )}
        </div>

        {/* 说明文字 */}
        {(status === 'ready' || status === 'checking') && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              {/* <p>1. 在微信里打开XiD Math小程序</p>
              <p>2. 选择首页--扫码登录</p>
              <p>3. 扫描上方二维码完成登录</p> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRLoginModal;